import clsx from 'clsx'
import first from 'lodash/first'
import noop from 'lodash/noop'
import React, {
  createContext,
  FC,
  MutableRefObject,
  PropsWithChildren,
  ReactNode,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle, useMemo,
  useRef,
  useState,
} from 'react'

import SvgClose from '@Assets/SVG/Close'
import { enableBodyScroll, preventBodyScroll } from '@Globals/BodyHelpers'
import { useSplitTest } from '@Globals/SplitTests/Hooks'
import { Spinner } from '@Shared/Components/Spinner/Spinner'

import styles from './NativeModal.module.scss'

export type NativeModalActions = {
  close: () => void
  open: () => void
  runReloadAnimation: () => void
}

export const useNativeModalActions = () => {
  const ref = useRef<NativeModalActions | null>(null)
  return ref
}

const CloseContext = createContext<(fromOnExit?: boolean) => void >(noop)
export const NATIVE_MODAL_OPEN_CLASS = 'NativeModalOpen'

export interface NativeModalProps {
  actionRef?: MutableRefObject<NativeModalActions | null>
  alignment?: 'left' | 'center' | 'right' | 'top' | 'bottom'
  backdrop?: boolean
  className?: string
  closeOnBackDrop?: boolean
  closeOnEscape?: boolean
  defaultOpen?: boolean
  onClose?: () => void
  onOpen?: () => void
  prerender?: boolean
}

export const NativeModalBody = ({
  barebones = false,
  children,
  className,
}: {
  barebones?: boolean
  children: ReactNode
  className?: string
}) => (
  <div className={clsx(styles.modalBody, className, 'modalBody', barebones && styles.barebones)}>
    <div className={styles.inner}>
      {children}
    </div>
  </div>
)

export const NativeModalCloseButton = () => {
  const close = useContext(CloseContext)
  return (
    <button
      aria-label="Close"
      className={styles.closeButton}
      onClick={(e) => {
        e.stopPropagation()
        e.preventDefault()
        close(false)
      }}
      type="button"
    >
      <SvgClose />
    </button>
  )
}

export const NativeModalHeader: FC<PropsWithChildren<{ className?: string, float?: boolean }>> = ({
  children,
  className,
  float,
}) => {
  const close = useContext(CloseContext)
  return (
    <div className={clsx(styles.modalHeader, className, 'modalHeader', !children && styles.noChildren, float && styles.float)}>
      {children}
      <button
        aria-label="Close"
        className={styles.close}
        onClick={(e) => {
          e.stopPropagation()
          e.preventDefault()
          close(false)
        }}
        type="button"
      >
        <SvgClose />
      </button>
    </div>
  )
}

export const NativeModalFooter = ({
  barebones,
  children,
  className,
}: {
  barebones?: boolean
  children: ReactNode
  className?: string
}) => (
  <div className={clsx(styles.modalFooter, className, barebones && styles.barebones, 'modalFooter')}>
    {children}
  </div>
)

// TODO: Implement proper dialog solution when we can support all IOS versions, Polyfill did not work as of 09/04/2024
export const NativeModal: FC<PropsWithChildren<NativeModalProps>> = ({
  actionRef,
  alignment = 'center',
  backdrop,
  children,
  className,
  closeOnBackDrop = true,
  closeOnEscape = true,
  onClose = noop,
  onOpen = noop,
  defaultOpen = false,
  prerender,
}) => {
  const cofDesignV1 = useSplitTest('cof_design_v1', 'b')
  const [reloadingState, setReloadingState] = useState(0)
  const closeOnEscapeRef = useRef(closeOnEscape)
  const [isOpen, setIsOpen] = useState(false)
  const ref = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    closeOnEscapeRef.current = closeOnEscape
  }, [closeOnEscape])

  const close = useCallback((fromOnExit?: boolean) => {
    setIsOpen(false)
    setTimeout(() => {
      const dialogs = document.getElementsByClassName(NATIVE_MODAL_OPEN_CLASS)
      if (dialogs.length === 0) {
        enableBodyScroll()
      }
      if (!fromOnExit) {
        onClose()
      }
    }, 50)
  }, [onClose, setIsOpen])

  const initReloading = useCallback(() => {
    // moves it back in the execution stack
    setTimeout(() => setReloadingState(1), 0)
  }, [])

  const open = useCallback(() => {
    preventBodyScroll()
    setIsOpen(true)
    onOpen()
  }, [onOpen, setIsOpen])

  useImperativeHandle(actionRef, () => ({
    open: () => open && open(),
    close: () => close && close(true),
    runReloadAnimation: initReloading,
  }))

  useEffect(() => {
    if (defaultOpen) {
      setIsOpen(true)
      open()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onBackdropClick = (e) => {
    if (reloadingState) {
      return
    }
    if (!closeOnBackDrop || !ref.current) {
      return
    }
    const dialogDimensions = ref.current?.getBoundingClientRect()
    if (
      e.clientX < dialogDimensions.left
      || e.clientX > dialogDimensions.right
      || e.clientY < dialogDimensions.top
      || e.clientY > dialogDimensions.bottom
    ) {
      close()
    }
  }

  useEffect(() => {
    if (isOpen) {
      preventBodyScroll()
    }
  }, [isOpen])

  const onEscapeKey = useCallback((e) => {
    if (closeOnEscapeRef.current && (e.key === 'Escape' || e.key === 'Esc' || e.keyCode === 27)) {
      close()
    }
  }, [close])

  const initCloseEvents = useCallback(() => {
    if (closeOnEscape) {
      window.addEventListener('keyup', onEscapeKey)
    }
    return () => {
      // element.removeEventListener('click', onBackdropClick)
      window.removeEventListener('keyup', onEscapeKey)
    }
  }, [closeOnEscape, onEscapeKey])

  useEffect(() => {
    if (reloadingState) {
      setTimeout(() => {
        first(document.getElementsByClassName('modalBody'))
          ?.scrollTo({
            top: 0,
            behavior: 'instant',
          })
        setReloadingState(prev => (prev > 2 ? 0 : prev + 1))
      }, 300)
    }
  }, [reloadingState])

  // @ts-ignore
  return (
    <div className={clsx(styles.dialogWrapper, backdrop && styles.withBackdrop)}>
      {isOpen && backdrop && (
        <div
          className={clsx('backdrop', styles.backdrop)}
          onClick={onBackdropClick}
          onTouchMove={(e) => {
            e.stopPropagation()
          }}
          onTouchStart={(e) => {
            e.stopPropagation()
          }}
        />
      )}
      <CloseContext.Provider value={close}>
        <dialog
          className={clsx(
            className,
            styles.modal,
            'fixed',
            'modal',
            isOpen && styles.isOpen,
            isOpen && NATIVE_MODAL_OPEN_CLASS,
            (prerender && !isOpen) && styles.hidden,
            styles[alignment],
            reloadingState === 1 && styles.reloadingIn,
            reloadingState === 2 && styles.reloadingOut,
            cofDesignV1 && styles.cofDesignV1,
          )}
          open={isOpen}
          ref={(reference) => {
            if (reference) {
              initCloseEvents()
            }
            ref.current = reference!
          }}
        >
          {(isOpen || prerender) && children}
        </dialog>
      </CloseContext.Provider>
    </div>
  )
}

type ModalActions = {
  close: () => void
  open: (ui: React.ReactNode, props?: Partial<Omit<NativeModalProps, 'actionRef'>>) => void
  setContent: (ui: React.ReactNode) => void
  setProps: (props: Partial<Omit<NativeModalProps, 'actionRef'>>) => void
}
const ModalActionsContext = createContext<ModalActions | null>(null)
const NativeModalHost = ({ actionRef, register }: {
  actionRef: ReturnType<typeof useNativeModalActions>
  register: (impl: ModalActions) => void
}) => {
  const [ui, setUi] = useState<React.ReactNode>()
  const [modalProps, setModalProps] = useState<Partial<Omit<NativeModalProps, 'actionRef'>>>({
    backdrop: true,
    closeOnEscape: true,
    closeOnBackDrop: true,
    alignment: 'center',
  })

  const impl = useMemo<ModalActions>(() => ({
    open: (nextUi, nextProps) => {
      setModalProps(prev => nextProps ?? prev)
      setUi(nextUi)
      actionRef.current?.open?.()
    },
    setContent: nextUi => setUi(nextUi),
    setProps: nextProps => setModalProps(p => ({ ...p, ...nextProps })),
    close: () => {
      actionRef.current?.close?.()
      setUi(undefined)
      setModalProps({})
    },
  }), [actionRef])

  useEffect(() => { register(impl) }, [impl, register])

  return (
    <NativeModal actionRef={actionRef} {...modalProps}>
      <Suspense fallback={<Spinner />}>{ui}</Suspense>
    </NativeModal>
  )
}

export const NativeModalProvider = ({ children }: { children: React.ReactNode }) => {
  const actionRef = useNativeModalActions()
  const implRef = useRef<ModalActions>({
    close: noop,
    setProps: noop,
    setContent: noop,
    open: noop,
  })

  const actions = useMemo<ModalActions>(() => ({
    open: (...a) => implRef.current?.open(...a),
    setContent: (...a) => implRef.current?.setContent(...a),
    setProps: (...a) => implRef.current?.setProps(...a),
    close: () => implRef.current?.close(),
  }), [])

  return (
    <ModalActionsContext.Provider value={actions}>
      {children}
      <NativeModalHost actionRef={actionRef} register={(impl) => { implRef.current = impl }} />
    </ModalActionsContext.Provider>
  )
}

export const useNativeModal = () => {
  const ctx = useContext(ModalActionsContext)
  if (!ctx) { throw new Error('useNativeModal must be used within NativeModalProvider') }
  return ctx
}
